import type { RoleSummary } from '@/shared/types/domain';

/**
 * OBS-007 — the one order in which a person's own roles are read: the first
 * one on the header's role line (with "+N" for the others), then all of them,
 * in this order, in the account menu, the phone account sheet and My profile.
 *
 * PROVISIONAL — THE PRODUCT OWNER SETS THIS ORDER. No approved source defines
 * one (R3 decision table, OBS-007, option A: "in an order the Product Owner
 * sets"). Until that order is recorded, this is the order in which the server
 * declares the role templates every organisation is given (the backend's
 * SystemRole), so the product invents no ranking of its own. The Product
 * Owner's order replaces this list, and nothing else has to change.
 *
 * An order is not a hierarchy. The data holds no primary role, and coming
 * first here gives a role none of a primary role's meaning: the header names
 * the first and counts the rest, every list shows them all, and nothing offers
 * to switch between them. What a person may do is their permissions, which the
 * server decides for every action.
 *
 * A role the organisation created itself has no place in the list: it follows
 * the templates, in the order the server sent it.
 */
export const ROLE_ORDER: readonly string[] = [
  'client_admin',
  'procurement_manager',
  'purchaser',
  'branch_requester',
  'approver',
  'receiver',
  'warehouse_user',
  'finance',
  'read_only',
];

/** `roles` in ROLE_ORDER: a new array (a stable sort), the session's own left as it is. */
export function inRoleOrder<T extends Pick<RoleSummary, 'slug'>>(roles: readonly T[]): T[] {
  const rank = (role: T): number => {
    const at = ROLE_ORDER.indexOf(role.slug);

    return at === -1 ? ROLE_ORDER.length : at;
  };

  return [...roles].sort((a, b) => rank(a) - rank(b));
}
